import {formatDateLocale} from '@hconnect/uikit'
import {
  formatTimeLocale,
  QuickSelection
} from '@hconnect/uikit/src/lib2/components/momentDatePicker'
import {QuickSelectionItem} from '@hconnect/uikit/src/lib2/components/momentDatePicker/types'
import moment, {Moment} from 'moment-timezone'
import React, {useMemo} from 'react'

import {Shift, ShiftQuickSelectSlots} from '../../types'
import {
  roundUpToTheNearestMinute,
  getQuickSelectFilter,
  SHIFT_QUICK_SELECT_SLOTS_DEFAULT
} from '../../utils'

const getQuickSelectionItems = (
  quickSelectSlots: ShiftQuickSelectSlots[],
  shifts: Shift[],
  plantNow: Moment,
  translate: (key: string) => string
): QuickSelectionItem[] => {
  return quickSelectSlots.map((curr: ShiftQuickSelectSlots) => {
    const [start, end] = getQuickSelectFilter(curr, shifts, plantNow)
    return {
      id: curr,
      title: translate(`shiftEvent.quickSelection.${curr}`),
      dates: [start, end && roundUpToTheNearestMinute(end)]
    }
  })
}

const getDateLabel = ({
  translate,
  shiftStart,
  currentDate,
  language
}: {
  translate: (key: string) => string
  shiftStart: Moment
  currentDate: Moment
  language: string
}) => {
  const formattedDate = () => formatDateLocale(shiftStart, language)
  const quickSelectLabel = (key: string) => translate(`shiftEvent.quickSelection.${key}`)

  return {
    currentShift: () => quickSelectLabel('today'),
    pastShift: () => {
      const isToday = shiftStart.isSame(currentDate, 'date')
      const isYesterday = shiftStart.isSame(currentDate.clone().subtract(1, 'day'), 'date')
      if (!isToday && !isYesterday) {
        return formattedDate()
      }
      return isToday ? quickSelectLabel('today') : quickSelectLabel('yesterday')
    },
    today: () => quickSelectLabel('today'),
    last24hours: () => quickSelectLabel('last24hours'),
    yesterday: () => quickSelectLabel('yesterday'),
    lastWeekend: formattedDate,
    last3days: formattedDate,
    last7days: formattedDate,
    last30days: formattedDate,
    next30days: formattedDate,
    rolling12months: formattedDate
  }
}

type Props = {
  quickSelectId?: string
  onChange: (item: ShiftQuickSelectSlots) => void
  shifts: Shift[]
  timezone: string
  plantNow: Moment
  translate: (key: string) => string
  language: string
  quickSelectSlots?: ShiftQuickSelectSlots[]
}

export const QuickSelect: React.FC<Props> = ({
  quickSelectId,
  onChange,
  shifts,
  timezone,
  plantNow,
  translate,
  language,
  quickSelectSlots = SHIFT_QUICK_SELECT_SLOTS_DEFAULT
}) => {
  const state = useMemo(() => {
    const options: QuickSelectionItem[] = getQuickSelectionItems(
      Object.values(quickSelectSlots),
      shifts,
      plantNow,
      translate
    )

    const selectedItem = options?.filter((item) => {
      return item.id === quickSelectId
    })[0]

    const customTemplate = (option: QuickSelectionItem) => {
      const {id, dates} = option
      const [start, end] = dates
      const currentDate = moment.tz(timezone)

      const dateLabel =
        id && start
          ? getDateLabel({
              translate,
              shiftStart: start,
              currentDate,
              language
            })[id]()
          : translate(`shiftEvent.quickSelection.${id}`)
      const timeLabel =
        start && end
          ? `${start && formatTimeLocale(start)} - ${end && formatTimeLocale(end)}`
          : translate('shiftEvent.label.unavailable')
      return `${dateLabel} , ${timeLabel}`
    }
    return {options, selectedItem, customTemplate}
  }, [language, quickSelectId, shifts, translate, timezone, plantNow, quickSelectSlots])

  const {options, selectedItem, customTemplate} = state

  return (
    <QuickSelection
      handleItemClick={(_, quickSelectItem) => {
        onChange(quickSelectItem?.id as ShiftQuickSelectSlots)
      }}
      selectedItem={selectedItem}
      dateCustomTemplate={customTemplate}
      items={options}
      timezone={timezone}
      translate={translate}
      locale={language}
      getTooltipMessage={(isDisabled) => isDisabled && translate('shiftEvent.label.shiftNotExist')}
    />
  )
}
